// @ts-nocheck
import styles from "./QRPay.module.scss";
import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { Modal } from "UI/Modal/Modal";
import { QRCodeSVG } from "qrcode.react";
import useUrlData from "store/DataStore";
import { getDeviceInfo, copyToClipboard } from "common/commonFunctions";
import useDeviceStore from "store/DeviceStore";
import useErrorStore from "store/errorStore";
import { getOrderData } from "services/api";
import { Loader } from "UI/Loader/Loader";
import { trackPaymentClick } from "services/analytics";
import useTimerData from "store/TimerStore";

const POLLING_INTERVAL = 10000;

const QRPay = () => {
  const { t } = useTranslation();
  const { orderId } = useParams<{ orderId: string }>();
  const { data: allData, loading, error, fetchOrderData, mapServerData, setMockData } = useUrlData();
  const [isPay, setIsPay] = useState(false);
  const navigate = useNavigate();
  const [onCancel, setOnCancel] = useState(false);
  const { setDevice } = useDeviceStore();
  const { networkErr } = useErrorStore((state) => state);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const { calculateRemaining, setExpiresAt, decreaseTimer } = useTimerData();
  const [timerDisplay, setTimerDisplay] = useState("00:00");

  useEffect(() => {
    if (allData.expiresAt) {
      setExpiresAt(allData.expiresAt);
    }
    const interval = setInterval(() => {
      decreaseTimer();
      const remaining = calculateRemaining();
      const mins = Math.floor(remaining / 60);
      const secs = remaining % 60;
      setTimerDisplay(`${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`);
    }, 1000);
    return () => clearInterval(interval);
  }, [allData.expiresAt, setExpiresAt, decreaseTimer, calculateRemaining]);

  useEffect(() => {
    if (orderId === 'mock') {
      setMockData({
        payment_id: "88770f35-e539-5667-b1gg-cd8824e4cd5f",
        amount: 7500,
        currency: "ARS",
        address: "0000154600000000172499",
        qrData: "00020101021243650016COM.MERCADOLIBRE",
        bank_name: "Naranja X",
        status_name: "pending",
      });
    }
  }, [orderId, setMockData]);

  const checkStatusAndRedirect = useCallback((status: string) => {
    const s = status.toLowerCase();
    if (s === 'expired') { navigate(`/canceled/${orderId}?type=expired`, { replace: true }); return true; }
    if (s === 'cancelled' || s === 'canceled') { navigate(`/canceled/${orderId}?type=canceled`, { replace: true }); return true; }
    if (s === 'completed' || s === 'finished' || s === 'success') { navigate(`/success/${orderId}`, { replace: true }); return true; }
    return false;
  }, [navigate, orderId]);

  useEffect(() => {
    if (orderId && orderId !== 'mock' && !allData.payment_id) {
      fetchOrderData(orderId).catch(console.error);
    }
  }, [orderId, fetchOrderData, allData.payment_id]);

  useEffect(() => {
    if (!loading && allData.status_name) checkStatusAndRedirect(allData.status_name);
  }, [loading, allData.status_name, checkStatusAndRedirect]);

  useEffect(() => {
    if (!orderId || orderId === 'mock') return;
    const poll = async () => {
      try {
        const data = await getOrderData(orderId);
        mapServerData(data);
        if (data.state) checkStatusAndRedirect(data.state);
      } catch (e) { console.error(e); }
    };
    const id = setInterval(poll, POLLING_INTERVAL);
    return () => clearInterval(id);
  }, [orderId, mapServerData, checkStatusAndRedirect]);

  useEffect(() => { getDeviceInfo(setDevice); }, [allData, networkErr, setDevice]);

  const handleCopy = async (text: string, field: string) => {
    const success = await copyToClipboard(text);
    if (success) {
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    }
  };

  const formatAmount = (amount: number) => amount.toLocaleString('es-AR');

  const handleMercadoPago = async () => {
    if (!allData.address) return;
    const success = await copyToClipboard(allData.address);
    if (success) {
      setCopiedField('cbu');
      setTimeout(() => setCopiedField(null), 2000);
    }
    
    const isAndroid = /android/i.test(navigator.userAgent);
    if (isAndroid) {
      window.location.href = 'mercadopago://money-out/transfers/unified/home?component=input_pesos';
    } else {
      window.location.href = 'mercadopago://money-out/transfers/unified/input';
    }
  };

  const qrValue = allData.qrData || allData.address || '';

  if (loading) {
    return <div className={styles.page}><div className={styles.center}><Loader /></div></div>;
  }

  if (error) {
    return (
      <div className={styles.page}>
        <div className={styles.center}>
          <p className={styles.errorText}>{error}</p>
          <button onClick={() => navigate("/")} className={styles.btn}>{t("Volver")}</button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      {/* Header */}
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <span className={styles.logo}>PagoPay</span>
        </div>
        <div className={styles.headerRight}>
          <span className={styles.timer}>{timerDisplay}</span>
        </div>
      </header>

      {/* Amount Section */}
      <section className={styles.amountSection}>
        <div className={styles.amountLabel}>Monto a transferir</div>
        <div className={styles.amountValue}>$ {formatAmount(allData.amount)}</div>
      </section>

      {/* QR Section */}
      <section className={styles.qrSection}>
        <div className={styles.qrBox}>
          <QRCodeSVG size={120} value={qrValue} level="H" bgColor="#FFFFFF" fgColor="#000000" />
        </div>
        <div className={styles.qrText}>Escanee el código QR con su app bancaria</div>
      </section>

      {/* Data Section */}
      <section className={styles.dataSection}>
        <div className={styles.dataRow}>
          <span className={styles.dataLabel}>Banco</span>
          <span className={styles.dataValue}>{allData.bank_name || 'Naranja X'}</span>
          <img 
            src={allData.bank_name?.toLowerCase().includes('personal') ? '/banks/personalpay.svg' : '/banks/nx.png'} 
            alt="" 
            className={styles.bankIcon}
          />
        </div>
        <div className={styles.dataRow}>
          <span className={styles.dataLabel}>CBU</span>
          <span className={styles.dataValue}>{allData.address || '—'}</span>
          <button 
            className={styles.copyBtn} 
            onClick={() => handleCopy(allData.address || '', 'cbu')}
          >
            {copiedField === 'cbu' ? 'Copiado' : 'Copiar'}
          </button>
        </div>
        <div className={styles.dataRow}>
          <span className={styles.dataLabel}>Monto</span>
          <span className={styles.dataValue}>$ {formatAmount(allData.amount)}</span>
          <button 
            className={styles.copyBtn} 
            onClick={() => handleCopy(String(allData.amount), 'amount')}
          >
            {copiedField === 'amount' ? 'Copiado' : 'Copiar'}
          </button>
        </div>
      </section>

      {/* Actions */}
      <section className={styles.actions}>
        <button className={styles.btnMercado} onClick={handleMercadoPago}>
          <img src="/MPlogo.png" alt="" />
          <span>Abrir Mercado Pago</span>
        </button>
        <button 
          className={styles.btnSuccess} 
          onClick={() => { if (orderId) trackPaymentClick(orderId); setIsPay(true); }}
        >
          Ya realicé el pago
        </button>
        <button className={styles.btnLink} onClick={() => setOnCancel(true)}>
          Cancelar
        </button>
      </section>

      {/* Footer */}
      <footer className={styles.footer}>
        <span>Transacción segura • ID: {orderId?.slice(0, 8)}</span>
      </footer>

      {onCancel && (
        <Modal
          type="cancel"
          goTo={() => navigate(`/canceled/${orderId}?type=canceled`)}
          onClose={() => setOnCancel(false)}
        />
      )}
      {isPay && navigate(`/searchPay/${orderId}`)}
    </div>
  );
};

export default QRPay;
